import * as TWEEN from 'three/addons/libs/tween.module.js';
import type { Object3D } from 'three';

const group = new TWEEN.Group();

export interface Vec3Target {
  x?: number;
  y?: number;
  z?: number;
}

export function tweenTo(obj: Object3D, target: Vec3Target, durationMs = 220): TWEEN.Tween<Object3D['position']> {
  return new TWEEN.Tween(obj.position, group)
    .to(
      {
        x: target.x ?? obj.position.x,
        y: target.y ?? obj.position.y,
        z: target.z ?? obj.position.z,
      },
      durationMs,
    )
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

export function tweenArc(
  obj: Object3D,
  target: { x: number; y: number; z: number },
  durationMs = 260,
  peakLift = 0.3,
): TWEEN.Tween<{ t: number }> {
  const start = { x: obj.position.x, y: obj.position.y, z: obj.position.z };
  const proxy = { t: 0 };
  return new TWEEN.Tween(proxy, group)
    .to({ t: 1 }, durationMs)
    .easing(TWEEN.Easing.Cubic.Out)
    .onUpdate(() => {
      const t = proxy.t;
      obj.position.x = start.x + (target.x - start.x) * t;
      obj.position.z = start.z + (target.z - start.z) * t;
      const baseY = start.y + (target.y - start.y) * t;
      obj.position.y = baseY + peakLift * Math.sin(Math.PI * t);
    })
    .onComplete(() => {
      obj.position.set(target.x, target.y, target.z);
    })
    .start();
}

export function tweenRotation(
  obj: Object3D,
  target: Vec3Target,
  durationMs = 220,
): TWEEN.Tween<Object3D['rotation']> {
  const to: Record<string, number> = {};
  if (target.x !== undefined) to.x = target.x;
  if (target.y !== undefined) to.y = target.y;
  if (target.z !== undefined) to.z = target.z;
  return new TWEEN.Tween(obj.rotation, group)
    .to(to, durationMs)
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

export function tweenScale(
  obj: Object3D,
  target: number,
  durationMs = 220,
): TWEEN.Tween<Object3D['scale']> {
  return new TWEEN.Tween(obj.scale, group)
    .to({ x: target, y: target, z: target }, durationMs)
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

export function tweenFade(
  obj: Object3D,
  target: number,
  durationMs = 260,
  onComplete?: () => void,
): void {
  const proxy = { t: 0 };
  const startOpacity = new Map<Object3D, number>();
  obj.traverse((c) => {
    const m = (c as { material?: { opacity?: number; transparent?: boolean } }).material;
    if (m && typeof m.opacity === 'number') {
      startOpacity.set(c, m.opacity);
      m.transparent = true;
    }
  });
  new TWEEN.Tween(proxy, group)
    .to({ t: 1 }, durationMs)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onUpdate(() => {
      obj.traverse((c) => {
        const start = startOpacity.get(c);
        if (start === undefined) return;
        const m = (c as unknown as { material: { opacity: number } }).material;
        m.opacity = start + (target - start) * proxy.t;
      });
    })
    .onComplete(() => onComplete?.())
    .start();
}

export function updateTweens(timeMs: number) {
  group.update(timeMs);
}

export function clearTweens() {
  group.removeAll();
}

export function finishAllTweens() {
  const all = group.getAll().slice();
  for (const tween of all) tween.end();
}

export function hasActiveTweens(): boolean {
  return group.getAll().length > 0;
}
